import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, KeyRound, Eye, EyeOff, AlertCircle, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { translateError } from "../utils/i18nErrors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError(t("auth.passwordTooShort")); return; }
    if (password !== confirmPassword) { setError(t("auth.passwordMismatch")); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      setError(translateError(err.response?.data?.detail || "", t) || t("errors.unknownError"));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div data-testid="reset-password-page" className="min-h-screen lg:min-h-[calc(100vh-5rem)] bg-gradient-to-br from-slate-50 to-white flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 p-8 text-center">
          <p className="text-sm text-slate-700">{t("auth.invalidToken")}</p>
          <Link to="/forgot-password" data-testid="reset-password-request-new-link" className="text-xs text-[#028CF8] font-medium hover:underline mt-3 inline-block">
            {t("auth.requestNewLink")}
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div data-testid="reset-password-page" className="min-h-screen lg:min-h-[calc(100vh-5rem)] bg-gradient-to-br from-slate-50 to-white flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 p-8 text-center">
          <div className="h-12 w-12 rounded-xl bg-green-50 flex items-center justify-center mb-3 mx-auto">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
          </div>
          <p data-testid="reset-password-success-message" className="text-sm text-slate-700 mb-5">{t("auth.passwordUpdated")}</p>
          <button
            data-testid="reset-password-go-to-login"
            onClick={() => navigate("/auth")}
            className="w-full h-11 btn-primary justify-center rounded-xl text-sm"
          >
            {t("auth.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reset-password-page" className="min-h-screen lg:min-h-[calc(100vh-5rem)] bg-gradient-to-br from-slate-50 to-white flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
          <div className="flex flex-col items-center mb-6 text-center">
            <div className="h-12 w-12 rounded-xl bg-[#028CF8]/10 flex items-center justify-center mb-3">
              <KeyRound className="h-6 w-6 text-[#028CF8]" />
            </div>
            <h1 className="font-bold text-lg text-slate-900">{t("auth.resetPassword")}</h1>
          </div>

          {error && (
            <div data-testid="reset-password-error" className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5 mb-4">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="reset-password-form" className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("auth.newPassword")}</label>
              <div className="relative">
                <input
                  data-testid="reset-password-new-input"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("auth.passwordPlaceholder")}
                  className="w-full h-11 rounded-lg border border-slate-200 px-4 pr-11 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/30 focus:border-[#028CF8] transition-all"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("auth.confirmPassword")}</label>
              <input
                data-testid="reset-password-confirm-input"
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t("auth.passwordPlaceholder")}
                className="w-full h-11 rounded-lg border border-slate-200 px-4 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/30 focus:border-[#028CF8] transition-all"
              />
            </div>
            <button
              type="submit"
              data-testid="reset-password-submit"
              disabled={loading}
              className="w-full h-11 btn-primary justify-center rounded-xl text-sm disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.resetPassword")}
            </button>
          </form>

          <div className="text-center mt-5">
            <Link to="/auth" data-testid="reset-password-back-to-login" className="text-xs text-[#028CF8] font-medium hover:underline">
              {t("auth.backToLogin")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
